// Image fit and ratio controls (ARCH-IMAGE-SLOT-PRESENTATION, INS #3365; DES2-image-ux.md §B).
// Only fit, ratio and the caption (a text slot) change; never pixels or crop handles. Fit is
// read-only until the image is available in every selected context, and cover is offered only
// when every selected slot is crop-safe. Ratio offers only the ratios every slot allows.
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { inspectCopy } from '@/i18n/ns/inspect';
import type { ImageFit, ImageRatio } from '../../../src/artifacts/images/values.mjs';

const FITS: ImageFit[] = ['contain', 'cover'];

function ValueLine({
  mixed,
  defaultLabel,
  pending = false,
}: {
  mixed: boolean;
  defaultLabel: string | null;
  /** The default is what a pending Reset will leave, not what is stored now. */
  pending?: boolean;
}) {
  const copy = useNamespaceCopy(inspectCopy);
  if (mixed) return <p className="pc-inspect-mixed">{copy.mixed}</p>;
  if (!defaultLabel) return null;
  return (
    <p className="pc-inspect-pres-current" data-kind={pending ? 'pending-default' : 'default'}>
      {formatCopy(copy.defaultValue, { value: defaultLabel })}
    </p>
  );
}

export function ImageFitControl({
  value,
  defaultFit,
  available,
  cropSafe,
  mixed,
  pending,
  clearing = false,
  disabled = false,
  onChoose,
}: {
  /** The stored fit, or null when none is stored (the default applies). */
  value: ImageFit | null;
  defaultFit: ImageFit;
  available: boolean;
  cropSafe: boolean;
  mixed: boolean;
  /** A choice made in this edit and not yet applied. */
  pending?: ImageFit | null;
  /** A Reset is pending: the default shows (never written); choosing the stored value cancels it. */
  clearing?: boolean;
  disabled?: boolean;
  onChoose: (fit: ImageFit) => void;
}) {
  const copy = useNamespaceCopy(inspectCopy);
  const label = (fit: ImageFit) => (fit === 'cover' ? copy.fitCover : copy.fitContain);
  const now = clearing ? defaultFit : (pending ?? (mixed ? null : (value ?? defaultFit)));
  return (
    <div className="pc-inspect-format" data-testid="inspect-image-fit">
      <ValueLine
        mixed={mixed && !pending && !clearing}
        defaultLabel={clearing || (!pending && value === null && !mixed) ? label(defaultFit) : null}
        pending={clearing}
      />
      <div className="pc-inspect-seg" role="radiogroup" aria-label={copy.imageFit}>
        <span className="pc-inspect-seg-label" aria-hidden="true">
          {copy.imageFit}
        </span>
        {FITS.map((fit) => (
          <button
            key={fit}
            type="button"
            role="radio"
            aria-checked={now === fit}
            data-choice={`fit:${fit}`}
            disabled={disabled || !available || (fit === 'cover' && !cropSafe)}
            onClick={() => {
              if (now !== fit) onChoose(fit);
            }}
          >
            {label(fit)}
          </button>
        ))}
      </div>
      {!available ? (
        <p className="pc-inspect-note" data-testid="inspect-image-not-ready">
          {copy.imageNotReady}
        </p>
      ) : (
        !cropSafe && (
          <p className="pc-inspect-note" data-testid="inspect-image-uncroppable">
            {copy.fitCoverUnsafe}
          </p>
        )
      )}
    </div>
  );
}

export function ImageRatioControl({
  value,
  defaultRatio,
  allowed,
  mixed,
  pending,
  clearing = false,
  disabled = false,
  onChoose,
}: {
  value: ImageRatio | null;
  defaultRatio: ImageRatio;
  allowed: readonly ImageRatio[];
  mixed: boolean;
  pending?: ImageRatio | null;
  /** A Reset is pending: the default shows (never written); choosing the stored value cancels it. */
  clearing?: boolean;
  disabled?: boolean;
  onChoose: (ratio: ImageRatio) => void;
}) {
  const copy = useNamespaceCopy(inspectCopy);
  const label = (r: ImageRatio) => (r === 'intrinsic' ? copy.ratioIntrinsic : r);
  const now = clearing ? defaultRatio : (pending ?? (mixed ? null : (value ?? defaultRatio)));
  return (
    <div className="pc-inspect-format" data-testid="inspect-image-ratio">
      <ValueLine
        mixed={mixed && !pending && !clearing}
        defaultLabel={
          clearing || (!pending && value === null && !mixed) ? label(defaultRatio) : null
        }
        pending={clearing}
      />
      <div className="pc-inspect-seg" role="radiogroup" aria-label={copy.imageRatio}>
        <span className="pc-inspect-seg-label" aria-hidden="true">
          {copy.imageRatio}
        </span>
        {allowed.map((r) => (
          <button
            key={r}
            type="button"
            role="radio"
            aria-checked={now === r}
            data-choice={`ratio:${r}`}
            disabled={disabled}
            onClick={() => {
              if (now !== r) onChoose(r);
            }}
          >
            {label(r)}
          </button>
        ))}
      </div>
    </div>
  );
}
